import { isValidColorHex } from '@trimble/utilities';

const colorStorageKey: string = 'custom-color-palette';
const maxStorageLen: number = 24;

/**
 * Gets the color palette from local storage, if it exists
 * @returns Array of all color hex values as a string, or an empty array if none exist
 */
export function getPaletteFromStorage(): string[] {
  if (typeof window === 'undefined') {
    return [];
  }
  const storageValue = window.localStorage.getItem(colorStorageKey);
  if (!storageValue) {
    return [];
  }

  let colors: string[] = [];
  try {
    colors = JSON.parse(storageValue) as string[];
  } catch {
    // parse will fail if bad data/format, so clear bad data and return empty array
    clearPalette();
    return [];
  }
  return colors;
}

/**
 * Sets a new string array representing the custom color palette
 * @param colors Array of RGB hex color values as strings
 */
export function setPalette(colors: string[]): void {
  if (typeof window !== 'undefined') {
    const value = JSON.stringify(colors);
    window.localStorage.setItem(colorStorageKey, value);
  }
}

/**
 * Appends a new color value to the stored array. Will create a new array if this is the first value.
 * Will also format the string with a leading '#' if it is not present for consistency
 * @param color The color hex string to append
 */
export function appendToPalette(color: string): void {
  if (!color.startsWith('#')) {
    color = `#${color}`;
  }
  if (!isValidColorHex(color)) {
    // do not add values that do not match the hex regex pattern
    return;
  }
  let storedColors = getPaletteFromStorage();
  if (storedColors.length === 0) {
    storedColors = [];
  }
  storedColors.push(color);
  while (storedColors.length > maxStorageLen) {
    // limit amount of added colors by removing the first (oldest) color(s) in the array
    storedColors.shift();
  }
  setPalette(storedColors);
}

/**
 * Clears the color palette storage
 */
export function clearPalette(): void {
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(colorStorageKey);
  }
}
